// src/pages/TeachingStaff.js

import React, { useState, useEffect } from 'react';
import '../styles/TeachingStaff.scss';
import { Helmet } from 'react-helmet-async';
import { Users, GraduationCap } from 'lucide-react';
import StructuredData from '../components/StructuredData';

const SkeletonCard = () => (
  <div className="profile-card skeleton">
    <div className="header">
      <div className="avatar skeleton-avatar"></div>
    </div>
    <div className="content">
      <div className="skeleton-text skeleton-title"></div>
      <div className="skeleton-text skeleton-position"></div>
      <div className="underline"></div>
      <div className="skeleton-text skeleton-description"></div>
      <div className="skeleton-text skeleton-description short"></div>
    </div>
  </div>
);

const ProfileCard = ({ name, position, description, image }) => {
  const defaultImage = "https://cdn-icons-png.flaticon.com/512/6596/6596121.png";
  
  // Fix image URL by removing gs:// if it exists
  const cleanImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('gs://')) {
      return url.replace('gs://', '');
    }
    return url;
  };
  
  const cleanedImage = cleanImageUrl(image);
  
  return (
    <div className="profile-card">
      <div className="header">
        <div className="avatar">
          <img 
            src={cleanedImage || defaultImage} 
            alt={name ? name.toUpperCase() : 'Staff Member'} 
            className="staff-image"
            onError={(e) => {
              e.target.src = defaultImage;
            }}
          /> 
        </div>
      </div>
      <div className="content">
        <h2>{name}</h2>
        <span className="position">{position}</span>
        <div className="underline"></div>
        {description && description.trim() && (
          <p className="description">{description}</p>
        )}
      </div>
    </div>
  );
};

const TeachingStaff = () => {
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStaffData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://vhserver.desync.in';
        const response = await fetch(`${apiUrl}/public/api/school/ptvn_goraul/staff/teaching`);
        const data = await response.json();
        
        if (data.success) {
          const processedStaff = data.staffs.map(staff => ({
            ...staff,
            pic: staff.pic ? staff.pic.replace('gs://', '') : null
          }));
          setStaffData(processedStaff);
        } else {
          setError('Failed to load staff data');
        }
      } catch (error) {
        console.error('Error fetching staff data:', error);
        setError('Failed to load staff data');
      } finally {
        setLoading(false);
      }
    };

    fetchStaffData();
  }, []);

  return (
    <div className="teaching-staff-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Meet Our Dedicated Teaching Staff - Prabhat Tara Vidya Niketan</title>
        <meta name="title" content="Meet Our Dedicated Teaching Staff - Prabhat Tara Vidya Niketan" />
        <meta name="description" content="Explore the profiles of our dedicated teaching staff at Prabhat Tara Vidya Niketan Goraul, committed to academic excellence and student development." />
        <meta name="keywords" content="Prabhat Tara Vidya Niketan, Teaching Staff, Principal, Teachers, School Administration, CBSE School, Goraul, Vaishali" />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/teaching-staff" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-inner">
          <span className="badge">
            <Users size={16} /> 50+ Qualified Mentors & Educators
          </span>
          <h1>Our Teaching Staff & Faculty</h1>
          <p>
            Meet the passionate educators and subject matter specialists dedicated to nurturing intellectual curiosity and moral character from Nursery to Class 10.
          </p>
        </div>
      </div>

      <div className="staff-content-wrapper">
        {loading ? (
          <div className="staff-grid">
            {[...Array(6)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : error ? (
          <div className="error-message">
            <h3>Unable to load faculty directory</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
          </div>
        ) : staffData.length === 0 ? (
          <div className="empty-message">
            <GraduationCap size={48} className="empty-icon" />
            <h3>Faculty Directory</h3>
            <p>Our teaching faculty profiles are being updated for the new academic session 2026-2027.</p>
          </div>
        ) : (
          <div className="staff-grid">
            {staffData.map((staff) => (
              <ProfileCard
                key={staff._id}
                name={staff.name}
                position={staff.position}
                description={staff.description}
                image={staff.pic}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeachingStaff;
